import PropTypes from "prop-types";
import DitlCard from "./DitlCard";
import DitlSectionHeader from "./DitlSectionHeader";
import DitlUrgentCard from "./DitlUrgentCard";
import DitlCallListCard from "./DitlCallListCard";
import DitlCardListSkeleton from "./DitlCardListSkeleton";
import DitlAllDoneCard from "./DitlAllDoneCard";
import { DitlCallButton, DitlGoToTasksButton, DitlPreviewButton } from "./DitlCardButtons";
import { DITL_CARDS, DITL_SECTIONS } from "./ditlCards";

const LIST_CLASSES =
  "tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-gap-[12px] tw-pl-[24px] tw-pr-[12px] tw-py-[12px]";

const renderButton = (button, previewLabel) => {
  if (button === "preview") return <DitlPreviewButton key={button} label={previewLabel} />;
  if (button === "call") return <DitlCallButton key={button} />;
  return <DitlGoToTasksButton key={button} />;
};

const renderCard = (id, defaultOpen, onDone) => {
  const card = DITL_CARDS[id];

  if (card.type === "task") {
    return (
      <DitlCard
        key={id}
        title={card.title}
        collapsedTitle={card.collapsedTitle}
        description={card.description}
        actionLabel={card.actionLabel}
        actionIcon={card.actionIcon}
        defaultOpen={defaultOpen}
        onDone={onDone}
      />
    );
  }
  if (card.type === "urgent") {
    return (
      <DitlUrgentCard
        key={id}
        title={card.title}
        collapsedTitle={card.collapsedTitle}
        description={card.description}
        defaultOpen={defaultOpen}
        onDone={onDone}
      />
    );
  }
  return (
    <DitlCallListCard
      key={id}
      title={card.title}
      collapsedTitle={card.collapsedTitle}
      description={card.description}
      descriptionVariant={card.descriptionVariant}
      actions={
        <div className="tw-flex tw-items-center tw-gap-[8px]">
          {card.buttons.map((button) => renderButton(button, card.previewLabel))}
        </div>
      }
      defaultOpen={defaultOpen}
      onDone={onDone}
    />
  );
};

const DitlCardList = ({ loading = false, cards, onCardDone }) => {
  if (loading) {
    return (
      <div className={LIST_CLASSES}>
        <DitlCardListSkeleton />
      </div>
    );
  }

  const pendingIds = cards.filter((card) => !card.completed).map((card) => card.id);
  const sections = DITL_SECTIONS.map((section) => ({
    ...section,
    cardIds: pendingIds.filter((id) => DITL_CARDS[id]?.section === section.id),
  })).filter((section) => section.cardIds.length > 0);
  const firstCardId = sections[0]?.cardIds[0];
  const allDone = cards.length > 0 && pendingIds.length === 0;

  return (
    <div className={LIST_CLASSES}>
      {allDone && <DitlAllDoneCard />}
      {sections.map(({ id, label, icon, iconClassName, cardIds }) => (
        <div key={id} className="tw-flex tw-flex-col tw-gap-[8px]">
          <DitlSectionHeader icon={icon} iconClassName={iconClassName} label={label} />
          {cardIds.map((cardId) => renderCard(cardId, cardId === firstCardId, () => onCardDone(cardId)))}
        </div>
      ))}
    </div>
  );
};

DitlCardList.propTypes = {
  loading: PropTypes.bool,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      completed: PropTypes.bool,
    }),
  ).isRequired,
  onCardDone: PropTypes.func.isRequired,
};

export default DitlCardList;
